import { useState, useEffect, useCallback } from "react";
import {
  FiSearch, FiX, FiCheck, FiPlus, FiMinus, FiTrash2,
  FiCalendar, FiEdit2, FiAlertTriangle, FiPrinter,
} from "react-icons/fi";
import { apiGet, apiPost } from "../api";
import { Btn, Pill, Icon, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";
import { printRestockInvoice } from "./RestockInvoices";

/* ─── helpers ─── */
const MMK      = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const numOnly  = (s) => (String(s).match(/\d/g) || []).join("");
const today    = () => new Date().toISOString().split("T")[0];
const initials = (name) => name.split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();

const YUAN_RATE_DEFAULT = 580;

const lbl      = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 6 };
const fieldBox = { display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 12px", background: "var(--surface)" };
const sqbtn    = { width: 30, height: 30, borderRadius: 8, border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}

/* ─── Step 1: Supplier picker ─── */
function SupplierStart({ suppliers, onPick, onCancel }) {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const matches = suppliers.filter(x =>
    !s || x.name.toLowerCase().includes(s) || (x.contact || "").toLowerCase().includes(s)
  );

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 484, padding: "24px 26px 22px", display: "flex", flexDirection: "column", maxHeight: "84vh" }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>Receive Stock</span>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>ပစ္စည်းဖြည့်</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 4 }}>Step 1 of 2 · Choose the supplier you're buying from</div>
          </div>
          <button onClick={onCancel} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex" }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        <div style={{ ...fieldBox, padding: "11px 14px", marginBottom: 14 }}>
          <FiSearch size={18} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search supplier by name or contact…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", width: "100%" }} />
        </div>

        <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 8 }}>
          Suppliers {q && `· ${matches.length} found`}
        </div>

        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 2, minHeight: 60 }}>
          {matches.length === 0 && <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13.5, padding: "24px 0" }}>No suppliers match "{q}".</div>}
          {matches.map(x => (
            <button key={x.id} onClick={() => onPick(x)}
              style={{ display: "flex", alignItems: "center", gap: 13, width: "100%", textAlign: "left", padding: "11px 14px", borderRadius: "var(--r-md)", border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--blue-50)"; e.currentTarget.style.borderColor = "var(--blue-100)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--line)"; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 800, color: "var(--primary)", flexShrink: 0 }}>
                {initials(x.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 14.5, fontWeight: 700, color: "var(--ink)" }}>{x.name}</div>
                <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 2 }}>{x.contact || "No contact"}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Success modal ─── */
function ReceiveSuccess({ restockId, totalMMK, itemCount, supplierName, token, go, onNew }) {
  const [printing, setPrinting] = useState(false);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const res = await apiGet(`/api/inventory/restocks/${restockId}/invoice`, token);
      const inv = await res.json();
      if (res.ok) printRestockInvoice(inv);
    } catch {}
    finally { setPrinting(false); }
  };

  return (
    <div onClick={() => go("overview")} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 400, padding: "30px 28px 24px", textAlign: "center" }}>
        <div style={{ width: 62, height: 62, borderRadius: 99, background: "var(--success-bg)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <FiCheck size={30} color="var(--success)" strokeWidth={2.4} />
        </div>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--ink)" }}>Stock received</div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 6 }}>Stock updated · movement logged</div>

        <div style={{ background: "var(--faint)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "16px 18px", margin: "20px 0", textAlign: "left" }}>
          {[["Reference", `#${restockId}`], ["Supplier", supplierName], ["Items", itemCount]].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 7 }}>
              <span style={{ color: "var(--sub)" }}>{k}</span>
              <span style={{ fontWeight: 600 }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700 }}>Total cost</span>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
              {MMK(totalMMK)} <span style={{ fontSize: 12, color: "var(--sub)" }}>MMK</span>
            </span>
          </div>
        </div>

        <Btn kind="ghost" onClick={handlePrint} disabled={printing} style={{ width: "100%", justifyContent: "center", marginBottom: 10 }}>
          <FiPrinter size={15} /> {printing ? "Preparing…" : "Print Invoice"}
        </Btn>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn kind="ghost" onClick={onNew} style={{ flex: 1, justifyContent: "center" }}>New Receiving</Btn>
          <Btn kind="pri" onClick={() => go("overview")} style={{ flex: 1, justifyContent: "center" }}>Done</Btn>
        </div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function Restock({ token, go, homeLocation }) {
  const [products, setProducts]     = useState([]);
  const [suppliers, setSuppliers]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);

  const [started, setStarted]     = useState(false);
  const [supplier, setSupplier]   = useState(null);

  const [recvDate, setRecvDate]   = useState(today());
  const [currency, setCurrency]   = useState("YUAN"); // "YUAN" | "MMK"
  const [rate, setRate]           = useState(String(YUAN_RATE_DEFAULT));
  const [payMethod, setPayMethod] = useState("cash");
  const [transportFee, setTransportFee] = useState("");
  const [lines, setLines]         = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [prodCat, setProdCat]     = useState("all");
  const [prodPage, setProdPage]   = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const qs = homeLocation?.id ? `?location_id=${homeLocation.id}` : "";
      const [sr, pr, cr] = await Promise.all([
        apiGet("/api/inventory/products/restock", token).then(r => r.json()),
        apiGet(`/api/inventory/products/by-location${qs}`, token).then(r => r.json()),
        apiGet("/api/inventory/categories", token).then(r => r.json()),
      ]);
      setSuppliers(Array.isArray(sr) ? sr : []);
      setProducts(Array.isArray(pr) ? pr : []);
      setCategories(Array.isArray(cr) ? cr : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token, homeLocation?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── computed ── */
  const rateNum    = currency === "MMK" ? 1 : (parseFloat(rate) || 0);
  const costMMK    = (yuan) => Math.round((parseFloat(yuan) || 0) * rateNum);
  const totalYuan  = lines.reduce((s, l) => s + l.qty * (parseFloat(l.yuan) || 0), 0);
  const transportFeeNum = parseFloat(transportFee) || 0;
  const totalMMKv  = lines.reduce((s, l) => s + l.qty * costMMK(l.yuan), 0) + transportFeeNum;

  const pickSupplier = (sup) => {
    setSupplier(sup);
    setStarted(true);
    setError("");
    setLines([]);
  };

  /* ── line helpers ── */
  const addProduct = useCallback((p) => {
    setLines(ls => ls.find(l => l.productId === p.id)
      ? ls.map(l => l.productId === p.id ? { ...l, qty: l.qty + 1 } : l)
      : [...ls, { productId: p.id, qty: 1, yuan: "", sell: String(p.price) }]);
    setError("");
  }, []);

  const bumpQty  = (id, d) => setLines(ls => ls.map(l => l.productId === id ? { ...l, qty: Math.max(1, l.qty + d) } : l));
  const setQty   = (id, v) => { const n = Math.max(1, parseInt(numOnly(v) || "1", 10) || 1); setLines(ls => ls.map(l => l.productId === id ? { ...l, qty: n } : l)); };
  const setYuan  = (id, v) => setLines(ls => ls.map(l => l.productId === id ? { ...l, yuan: v.replace(/[^\d.]/g, "") } : l));
  const removeLn = (id)    => setLines(ls => ls.filter(l => l.productId !== id));

  /* ── submit ── */
  const handleReceive = async () => {
    if (lines.length === 0) return setError("Add at least one product to receive.");
    if (currency === "YUAN" && rateNum <= 0) return setError("Enter a valid exchange rate.");
    setSubmitting(true); setError("");
    try {
      const goodsCost = lines.reduce((s, l) => s + l.qty * costMMK(l.yuan), 0);
      const body = {
        employee_token: token,
        supplier_id: supplier?.id,
        note: currency === "YUAN" ? `Rate ¥1=${rateNum} Ks · Paid by ${payMethod}` : `Cost in MMK · Paid by ${payMethod}`,
        total_cost: Math.round(goodsCost),
        transportation_fee: transportFeeNum,
        products: lines.map(l => ({ product_id: l.productId, quantity: l.qty, unit_price: costMMK(l.yuan) })),
      };
      const res = await apiPost("/api/inventory/products/restock", body, token);
      if (res.ok) {
        const data = await res.json();
        setSuccess({ restockId: data.restock_id, totalMMK: totalMMKv, itemCount: lines.length, supplierName: supplier?.name || "—" });
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to receive stock.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSubmitting(false); }
  };

  const resetRestock = () => {
    setStarted(false); setSupplier(null); setLines([]);
    setCurrency("YUAN"); setRate(String(YUAN_RATE_DEFAULT)); setPayMethod("cash");
    setTransportFee("");
    setRecvDate(today()); setError(""); setSuccess(null);
  };

  const pq = prodQuery.trim().toLowerCase();
  const gridProducts = products.filter(p =>
    (prodCat === "all" || String(p.category) === prodCat) &&
    (!pq || p.name.toLowerCase().includes(pq) || (p.sku || "").toLowerCase().includes(pq))
  );
  const gridPage = gridProducts.slice((prodPage - 1) * PER_PAGE_DEFAULT, prodPage * PER_PAGE_DEFAULT);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

      {/* ── LEFT: product search + grid ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 27, fontWeight: 800, letterSpacing: "-.025em", color: "var(--ink)", marginBottom: 4 }}>
          Receive Stock <span style={{ fontFamily: "var(--font-mm)", fontSize: 16, fontWeight: 500, color: "var(--n-400)" }}>ပစ္စည်းဖြည့်</span>
        </div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginBottom: 18 }}>
          Search by name or SKU, filter by category, or tap a product below
        </div>

        {/* product search */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...fieldBox, padding: "11px 14px" }}>
            <FiSearch size={18} color="var(--n-400)" style={{ flexShrink: 0 }} />
            <input value={prodQuery} onChange={e => { setProdQuery(e.target.value); setProdPage(1); }} placeholder="Search product name or SKU…"
              style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", width: "100%" }} />
            {prodQuery && <button onClick={() => { setProdQuery(""); setProdPage(1); }} style={{ border: "none", background: "transparent", cursor: "pointer" }}><FiX size={15} color="var(--n-400)" /></button>}
          </div>
        </div>

        {/* category filter chips */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[{ id: "all", name: "All" }, ...categories].map(c => {
            const on = prodCat === String(c.id);
            return (
              <button key={c.id} onClick={() => { setProdCat(String(c.id)); setProdPage(1); }}
                style={{ padding: "7px 14px", borderRadius: 999, cursor: "pointer", border: `1px solid ${on ? "var(--primary)" : "var(--line)"}`, background: on ? "var(--primary)" : "var(--surface)", color: on ? "#fff" : "var(--n-600)", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600 }}>
                {c.name}
              </button>
            );
          })}
        </div>

        {/* product grid */}
        {gridProducts.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13.5, padding: "40px 0" }}>
            No products match{prodQuery ? ` "${prodQuery}"` : " this filter"}.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
          {gridPage.map(p => {
            const inList = lines.find(l => l.productId === p.id);
            const st = stockStatus(p);
            return (
              <button key={p.id} onClick={() => addProduct(p)}
                style={{ display: "flex", flexDirection: "column", textAlign: "left", background: "var(--surface)", border: `1px solid ${inList ? "var(--primary)" : "var(--line)"}`, borderRadius: "var(--r-md)", padding: "14px 16px", cursor: "pointer", boxShadow: "var(--shadow-sm)" }}
                onMouseEnter={e => { if (!inList) e.currentTarget.style.borderColor = "var(--blue-100)"; }}
                onMouseLeave={e => { if (!inList) e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25, minHeight: 34, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.name}</div>
                  <span style={{ width: 23, height: 23, borderRadius: 7, background: inList ? "var(--primary)" : "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {inList ? <FiCheck size={14} color="#fff" /> : <FiPlus size={14} color="var(--primary)" />}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7 }}>
                  <Pill tone={st}>{p.quantity}</Pill>
                  <span style={{ fontSize: 11.5, color: "var(--sub)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.category_name || "—"}</span>
                </div>
              </button>
            );
          })}
        </div>
        <Pagination total={gridProducts.length} page={prodPage} onChange={setProdPage} />
      </div>

      {/* ── RIGHT: receiving panel ── */}
      <div style={{ width: 430, flexShrink: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", display: "flex", flexDirection: "column", position: "sticky", top: 0, maxHeight: "calc(100vh - 84px)", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Receiving</div>
          <span style={{ fontSize: 12.5, color: "var(--sub)" }}>Draft</span>
        </div>

        {/* scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

          {/* supplier */}
          <div style={{ padding: "14px 20px 6px" }}>
            <label style={lbl}>Supplier <span style={{ color: "var(--danger)" }}>· required</span></label>
            <div style={{ display: "flex", alignItems: "center", gap: 11, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 12px", background: "var(--surface)" }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 800, color: "var(--primary)", flexShrink: 0 }}>
                {supplier ? initials(supplier.name) : <Icon name="suppliers" size={16} stroke="var(--primary)" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{supplier ? supplier.name : "—"}</div>
                <div style={{ fontSize: 11.5, color: "var(--sub)" }}>{supplier ? (supplier.contact || "No contact") : "Choose a supplier"}</div>
              </div>
              <button onClick={() => setStarted(false)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 5 }}>
                <FiEdit2 size={14} color="var(--n-400)" />
              </button>
            </div>
          </div>

          {/* date */}
          <div style={{ padding: "10px 20px 4px" }}>
            <label style={lbl}>Receive date</label>
            <div style={fieldBox}>
              <FiCalendar size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
              <input type="date" value={recvDate} onChange={e => setRecvDate(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%" }} />
            </div>
          </div>

          {/* currency toggle */}
          <div style={{ padding: "10px 20px 6px" }}>
            <label style={lbl}>Cost currency</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["YUAN", "¥ Yuan"], ["MMK", "Ks MMK"]].map(([c, t]) => (
                <button key={c} onClick={() => setCurrency(c)}
                  style={{ flex: 1, padding: "9px", borderRadius: "var(--r-sm)", cursor: "pointer", border: `1px solid ${currency === c ? "var(--primary)" : "var(--line)"}`, background: currency === c ? "var(--blue-50)" : "var(--surface)", color: currency === c ? "var(--primary)" : "var(--n-600)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* exchange rate (Yuan only) */}
          {currency === "YUAN" && (
          <div style={{ padding: "10px 20px 4px" }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Exchange rate</label>
              <div style={fieldBox}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 700, color: "var(--sub)", flexShrink: 0 }}>¥1 =</span>
                <input value={rate} onChange={e => setRate(e.target.value.replace(/[^\d.]/g, ""))}
                  style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 700, color: "var(--ink)", width: "100%", fontVariantNumeric: "tabular-nums" }} />
                <span style={{ fontSize: 12, color: "var(--n-400)", fontWeight: 600 }}>Ks</span>
              </div>
            </div>
          </div>
          )}

          {/* payment method */}
          <div style={{ padding: "10px 20px 6px" }}>
            <label style={lbl}>Paid by</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["cash","Cash"],["bank","Bank"],["mobile","Mobile"]].map(([m, t]) => (
                <button key={m} onClick={() => setPayMethod(m)}
                  style={{ flex: 1, padding: "9px", borderRadius: "var(--r-sm)", cursor: "pointer", border: `1px solid ${payMethod === m ? "var(--primary)" : "var(--line)"}`, background: payMethod === m ? "var(--blue-50)" : "var(--surface)", color: payMethod === m ? "var(--primary)" : "var(--n-600)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* transportation fee */}
          <div style={{ padding: "10px 20px 6px" }}>
            <label style={lbl}>Transportation fee</label>
            <div style={fieldBox}>
              <input value={transportFee} onChange={e => setTransportFee(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0"
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 700, color: "var(--ink)", width: "100%", fontVariantNumeric: "tabular-nums" }} />
              <span style={{ fontSize: 12, color: "var(--n-400)", fontWeight: 600 }}>MMK</span>
            </div>
          </div>

          {/* line items */}
          <div style={{ padding: "10px 16px 6px" }}>
            <label style={{ ...lbl, paddingLeft: 4 }}>Items received</label>
            {lines.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13, padding: "26px 0" }}>
                No items yet — search or tap a product
              </div>
            )}
            {lines.map(l => {
              const p = products.find(x => x.id === l.productId) || {};
              const c = costMMK(l.yuan);
              const sell = parseInt(numOnly(l.sell) || "0", 10);
              const margin = c > 0 ? Math.round((sell - c) / c * 100) : null;
              return (
                <div key={l.productId} style={{ padding: "11px 8px", borderBottom: "1px solid var(--hair)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "var(--sub)", marginTop: 1 }}>{p.sku} · {p.quantity} in stock</div>
                    </div>
                    <button onClick={() => removeLn(l.productId)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4 }}>
                      <FiTrash2 size={14} color="var(--n-400)" />
                    </button>
                  </div>

                  {/* qty × yuan = mmk cost */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <button onClick={() => bumpQty(l.productId, -1)} style={sqbtn}><FiMinus size={13} color="var(--n-600)" /></button>
                      <input value={l.qty} onChange={e => setQty(l.productId, e.target.value)}
                        style={{ width: 46, height: 30, textAlign: "center", border: "1px solid var(--line)", borderRadius: 7, fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums", outline: "none" }} />
                      <button onClick={() => bumpQty(l.productId, 1)} style={sqbtn}><FiPlus size={13} color="var(--n-600)" /></button>
                    </div>
                    <span style={{ fontSize: 12, color: "var(--n-400)" }}>×</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, border: "1px solid var(--line)", borderRadius: 7, padding: "0 9px", height: 30, flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--sub)" }}>{currency === "MMK" ? "Ks" : "¥"}</span>
                      <input value={l.yuan} onChange={e => setYuan(l.productId, e.target.value)} placeholder="0"
                        style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 600, color: "var(--ink)", width: "100%", textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
                    </div>
                    {currency === "YUAN" && (
                      <>
                        <span style={{ fontSize: 12, color: "var(--n-400)" }}>=</span>
                        <div style={{ width: 78, textAlign: "right", fontSize: 12.5, fontWeight: 700, color: "var(--n-500)", fontVariantNumeric: "tabular-nums" }}>
                          {MMK(c)}<span style={{ fontSize: 10, color: "var(--n-400)" }}> Ks</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* sell price + margin + line total */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
                    <span style={{ fontSize: 11.5, color: "var(--sub)", fontWeight: 600 }}>Sell</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, border: "1px solid var(--line)", borderRadius: 7, padding: "0 9px", height: 28, width: 108, background: "var(--faint)" }}>
                      <input value={l.sell} readOnly title="Sell price is set on the product page, not here"
                        style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 700, color: "var(--sub)", width: "100%", textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "default" }} />
                      <span style={{ fontSize: 10.5, color: "var(--n-400)" }}>Ks</span>
                    </div>
                    {margin !== null && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: margin >= 0 ? "var(--success)" : "var(--danger)" }}>
                        {margin >= 0 ? "+" : ""}{margin}%
                      </span>
                    )}
                    <div style={{ flex: 1, textAlign: "right", fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                      {MMK(l.qty * c)}<span style={{ fontSize: 11, color: "var(--sub)", fontWeight: 600 }}> Ks</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* footer */}
        <div style={{ padding: "14px 20px 18px", borderTop: "1px solid var(--hair)", background: "var(--faint)", borderRadius: "0 0 var(--r-lg) var(--r-lg)", flexShrink: 0 }}>
          {currency === "YUAN" && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 7 }}>
              <span style={{ color: "var(--sub)" }}>Total in Yuan</span>
              <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>¥ {totalYuan.toLocaleString("en-US", { maximumFractionDigits: 1 })}</span>
            </div>
          )}
          {transportFeeNum > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 7 }}>
              <span style={{ color: "var(--sub)" }}>Transportation fee</span>
              <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{MMK(transportFeeNum)} Ks</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4, paddingTop: 11, borderTop: "1px solid var(--line)" }}>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>
              Total cost {currency === "YUAN" && <span style={{ fontSize: 11.5, color: "var(--sub)", fontWeight: 600 }}>· ¥1={rateNum} Ks</span>}
            </span>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
              {MMK(totalMMKv)} <span style={{ fontSize: 13, color: "var(--sub)" }}>MMK</span>
            </span>
          </div>
          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 11, color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>
              <FiAlertTriangle size={14} /> {error}
            </div>
          )}
          <Btn kind="pri" onClick={handleReceive} disabled={submitting || lines.length === 0}
            style={{ width: "100%", justifyContent: "center", height: 54, marginTop: 13, fontSize: 16 }}>
            <FiCheck size={18} /> {submitting ? "Saving…" : "Receive Stock"}
          </Btn>
        </div>
      </div>

      {/* ── modals ── */}
      {!started && (
        <SupplierStart suppliers={suppliers} onPick={pickSupplier} onCancel={() => go("overview")} />
      )}

      {success && (
        <ReceiveSuccess
          {...success}
          token={token}
          go={go}
          onNew={resetRestock}
        />
      )}
    </div>
  );
}
